import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { History, User, Building2, ArrowRight, RefreshCw, Lock } from "lucide-react";
import { NavLink } from "react-router-dom";
import { Users, FileText, ShieldCheck } from "lucide-react";

// Admin page — cross-record onboarding audit log. Filters by event / actor
// / record; each row deep-links back to the parent onboarding detail page.
function AdminSidebar() {
  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-sm text-sm font-medium flex items-center gap-2 transition-colors ${
      isActive ? "bg-[#0B132B] text-white" : "text-[#0B132B] hover:bg-[#0B132B]/5"
    }`;
  return (
    <aside className="lg:sticky lg:top-20 lg:h-fit">
      <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37] mb-3">Admin</div>
      <nav className="flex lg:flex-col gap-1">
        <NavLink to="/admin/users" className={linkCls}><Users size={15} /> Users</NavLink>
        <NavLink to="/admin/agencies" className={linkCls}><ShieldCheck size={15} /> Agencies</NavLink>
        <NavLink to="/admin/proposals" className={linkCls}><FileText size={15} /> All proposals</NavLink>
        <NavLink to="/admin/rates" className={linkCls}><FileText size={15} /> Rate sheet</NavLink>
        <NavLink to="/admin/production-rate-cards" className={linkCls} data-testid="admin-nav-production-rate-cards"><Lock size={15} /> Production rate cards</NavLink>
        <NavLink to="/admin/onboarding-audit" className={linkCls} data-testid="admin-nav-audit">
          <History size={15} /> Audit log
        </NavLink>
      </nav>
    </aside>
  );
}

export default function OnboardingAudit() {
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({ events: [], actors: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event: "", actor_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.event)    params.event = filters.event;
      if (filters.actor_id) params.actor_id = filters.actor_id;
      const { data } = await api.get("/admin/onboarding/audit", { params });
      setRows(data.records || []);
      setFacets(data.facets || { events: [], actors: [] });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load audit log"); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10" data-testid="onboarding-audit-page">
        <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-8">
          <AdminSidebar />
          <div>
            <div className="mb-6">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Administration</div>
              <h1 className="font-display text-4xl font-black text-[#0B132B] leading-none mt-1">Onboarding Audit Log</h1>
              <p className="text-sm text-[#6C757D] mt-2">
                Every stage transition, assignment, and testing action across all onboarding records.
              </p>
            </div>

            <Card className="bg-white border border-[#E9ECEF] rounded-sm p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="audit-filter-bar">
              <select
                data-testid="audit-filter-event"
                value={filters.event} onChange={(e) => setFilters((f) => ({ ...f, event: e.target.value }))}
                className="text-xs rounded-sm border border-[#E9ECEF] bg-white px-2 py-1.5 min-w-[160px]"
              >
                <option value="">All events</option>
                {facets.events.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
              <select
                data-testid="audit-filter-actor"
                value={filters.actor_id} onChange={(e) => setFilters((f) => ({ ...f, actor_id: e.target.value }))}
                className="text-xs rounded-sm border border-[#E9ECEF] bg-white px-2 py-1.5 min-w-[160px]"
              >
                <option value="">All actors</option>
                {facets.actors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <Button variant="outline" size="sm" onClick={load} className="rounded-sm h-8" data-testid="audit-refresh">
                <RefreshCw size={12} className="mr-1" /> Refresh
              </Button>
              <div className="ml-auto text-[11px] text-[#6C757D]">Showing <b className="text-[#0B132B]">{rows.length}</b></div>
            </Card>

            <Card className="bg-white border border-[#E9ECEF] rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#0B132B] text-white text-[10px] uppercase tracking-[0.14em]">
                      <th className="text-left px-3 py-2 font-semibold">When</th>
                      <th className="text-left px-3 py-2 font-semibold">Dealer</th>
                      <th className="text-left px-3 py-2 font-semibold">Event</th>
                      <th className="text-left px-3 py-2 font-semibold">Actor</th>
                      <th className="text-left px-3 py-2 font-semibold">Transition</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-[#6C757D]">Loading…</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-[#6C757D]">No entries match those filters.</td></tr>
                    ) : rows.map((r) => (
                      <tr key={r.id} className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA]" data-testid={`audit-row-${r.id}`}>
                        <td className="px-3 py-2 text-xs text-[#6C757D] whitespace-nowrap font-mono">{formatDate(r.created_at)}</td>
                        <td className="px-3 py-2">
                          <div className="text-xs font-bold text-[#0B132B]">{r.dealer_name || "—"}</div>
                          {r.agency && <div className="text-[10px] text-[#6C757D] flex items-center gap-1"><Building2 size={9}/> {r.agency}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className="inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-[#F1F3F5] text-[#0B132B]">{r.event}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-[#0B132B] flex items-center gap-1"><User size={10}/> {r.actor_name || "system"}</td>
                        <td className="px-3 py-2 text-[11px] text-[#6C757D] whitespace-nowrap">
                          {r.from_status && r.to_status
                            ? <span><b>{r.from_status}</b> → <b>{r.to_status}</b></span>
                            : Object.keys(r.detail || {}).length > 0
                              ? <span className="italic">{JSON.stringify(r.detail).slice(0, 60)}</span>
                              : "—"}
                        </td>
                        <td className="px-3 py-2">
                          <Link to={`/onboarding/${r.onboarding_id}`} data-testid={`audit-open-${r.id}`}
                                className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#D4AF37] hover:text-[#0B132B] whitespace-nowrap">
                            Open <ArrowRight size={9} className="inline"/>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
